/**
 * Pure helpers for the unfulfilled-orders queue page. Kept separate from the
 * component so the status semantics, age formatting, and summary math are unit-tested.
 */
import type { components } from '@/lib/api/types.gen';

export type OrderLine = components['schemas']['OrderLineView'];
export type MappingStatus = OrderLine['mappingStatus'];

export type LineRef = { storeId: string; orderId: string; lineId: string };

/** Why a garment was already printed off-system (audit only — drives no inventory). */
export type AlreadyPrintedReason = NonNullable<
  components['schemas']['MarkPrintedOffSystemRequest']['reason']
>;

/**
 * Operator-facing label per reason. Lives here rather than in the page that WRITES it, because the
 * order job card READS it back on the row (`alreadyPrintedReason` has always been on the view and was
 * rendered nowhere) — and two copies of four operator-facing strings is exactly how a label drifts
 * from the choice it describes.
 */
export const ALREADY_PRINTED_REASONS: { value: AlreadyPrintedReason; label: string }[] = [
  { value: 'MANUAL_SHEET', label: 'Printed on a manual gang sheet' },
  { value: 'HAND_REPRINT', label: 'Printed by hand / reprinted' },
  { value: 'LEFTOVER', label: 'Left over from a past run' },
  { value: 'CUSTOMER_SUPPLIED', label: 'Customer brought their own shirt' },
];

/**
 * The label for a stored reason. Falls back to the raw token rather than throwing or rendering
 * nothing: the field is a server enum, so an unknown value means the contract moved ahead of this
 * list, and showing `HAND_REPRINT` is still more use to an operator than showing blank.
 */
export function alreadyPrintedReasonLabel(reason: string | undefined): string | null {
  if (!reason) return null;
  return ALREADY_PRINTED_REASONS.find((r) => r.value === reason)?.label ?? reason;
}
/** A per-line explanation from a `/batches` error context (blocked / conflicts / missing). */
export type LineReason = LineRef & { reason: string };

/**
 * Batch-create failure that carries the API's per-line context. The `/batches` 4xx bodies list the
 * exact lines that couldn't be batched (`blocked`/`conflicts` with a `reason`; `missing` without),
 * but that context is a passthrough field the generated client doesn't type — so it's narrowed from
 * `unknown` (see {@link extractLineReasons}) and rendered per line, instead of the operator seeing
 * only the generic summary detail.
 */
export class BatchBuildError extends Error {
  readonly reasons: LineReason[];
  constructor(message: string, reasons: LineReason[]) {
    super(message);
    this.name = 'BatchBuildError';
    this.reasons = reasons;
  }
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null;
}

/**
 * Pull per-line reasons out of a `/batches` error envelope (`{ ok:false, error:{ blocked, ... } }`).
 * Defensive by construction — any shape that isn't the expected `{storeId,orderId,lineId,reason}`
 * row is skipped, so a malformed body degrades to the generic message rather than throwing.
 */
export function extractLineReasons(err: unknown): LineReason[] {
  if (!isRecord(err) || !isRecord(err.error)) return [];
  const body = err.error;
  const out: LineReason[] = [];
  const pushWithReason = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const b of arr) {
      if (
        isRecord(b) &&
        typeof b.storeId === 'string' &&
        typeof b.orderId === 'string' &&
        typeof b.lineId === 'string' &&
        typeof b.reason === 'string'
      ) {
        out.push({ storeId: b.storeId, orderId: b.orderId, lineId: b.lineId, reason: b.reason });
      }
    }
  };
  pushWithReason(body.blocked);
  pushWithReason(body.conflicts);
  if (Array.isArray(body.missing)) {
    for (const m of body.missing) {
      if (
        isRecord(m) &&
        typeof m.storeId === 'string' &&
        typeof m.orderId === 'string' &&
        typeof m.lineId === 'string'
      ) {
        out.push({
          storeId: m.storeId,
          orderId: m.orderId,
          lineId: m.lineId,
          reason: 'Line no longer exists — refresh the queue.',
        });
      }
    }
  }
  return out;
}

export type StatusMeta = {
  /** Operator-facing label — names the state the operator acts on, not the internal enum. */
  label: string;
  /** Tailwind classes for the badge tone. */
  className: string;
  /** Whether this state needs someone to do something before the line can print. */
  attention: boolean;
};

/**
 * Colour is meaning here: red = can't print it, amber = waiting on money/confidence,
 * neutral = recognised but not yet mapped (S2), green = ready to batch (S2+).
 */
export const STATUS_META: Record<MappingStatus, StatusMeta> = {
  MAPPED: {
    label: 'Ready',
    className:
      'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
    attention: false,
  },
  UNMAPPED: {
    label: 'Needs mapping',
    className: 'border-border bg-muted text-muted-foreground',
    attention: false,
  },
  PAYMENT_PENDING: {
    label: 'Unpaid',
    className:
      'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
    attention: true,
  },
  BLOCKED: {
    label: 'Blocked',
    className: 'border-destructive/40 bg-destructive/10 text-destructive',
    attention: true,
  },
  EXCLUDED: {
    label: 'Not DTF',
    className: 'border-border bg-muted text-muted-foreground',
    attention: false,
  },
};

/**
 * Decoration technique of a line — the /orders badge + filter (screen-print lane PR-3).
 *
 * ⚠️ ABSENT MEANS DTF, never "unknown". Every line that predates the field, and every line whose
 * product carries no mapping method, is DTF — that is what the server's `LineDecorationEnum` says and
 * a reader that branched on presence would leave the whole existing queue unbadged and unfilterable.
 */
export type Decoration = 'dtf' | 'screen-print' | 'mixed';

export function decorationOf(line: Pick<OrderLine, 'decoration'>): Decoration {
  return line.decoration === 'screen-print' || line.decoration === 'mixed'
    ? line.decoration
    : 'dtf';
}

/**
 * Badge copy + tone. DTF is deliberately given NO badge (it is the overwhelming default and a badge on
 * every row is noise); only the two techniques that change how the garment is produced are called out.
 *
 * ⚠️ Not `--primary`: that token is the same red as `--destructive` in this theme, so a primary-toned
 * badge reads as an error rather than a technique ([[primary-and-destructive-are-the-same-red]]).
 */
export const DECORATION_META: Record<Decoration, { label: string; className: string } | null> = {
  dtf: null,
  'screen-print': {
    label: 'Screen print',
    className:
      'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300',
  },
  mixed: {
    label: 'Screen print + DTF',
    className:
      'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300',
  },
};

/** The `/orders` technique filter. `all` is the default view — nothing is hidden unless asked. */
export type DecorationFilter = 'all' | Decoration;

/**
 * Narrow the queue to one technique.
 *
 * ⚠️ This feeds `readyLines` → `scopedReady` → "select all ready", which is the POINT: with the filter
 * on "DTF", Select-all provably cannot sweep up a screen-print line, so the owner's "staff must not
 * accidentally batch the limited edition" requirement is enforced at the selection as well as at the
 * resolver. Filtering only the rendered rows while leaving the selection helpers reading the unfiltered
 * list would look identical on screen and defeat the whole guard.
 */
export function filterByDecoration(
  lines: readonly OrderLine[],
  filter: DecorationFilter,
): OrderLine[] {
  if (filter === 'all') return [...lines];
  return lines.filter((l) => decorationOf(l) === filter);
}

/** Which techniques are actually present, so the filter never offers an option that matches nothing. */
/**
 * How many lines each print method has, for the filter chips.
 *
 * ⚠️ Counted BEFORE the technique filter is applied — a chip that showed its own filtered count would
 * read 0 on every method except the active one, which is exactly the information the chips exist to
 * give. Pass the store-filtered set: the counts should describe what the current view can reach.
 */
export function decorationCounts(lines: readonly OrderLine[]): Record<'all' | Decoration, number> {
  const out = { all: 0, dtf: 0, 'screen-print': 0, mixed: 0 };
  for (const l of lines) {
    out.all += 1;
    out[decorationOf(l)] += 1;
  }
  return out;
}

export function decorationOptions(lines: readonly OrderLine[]): Decoration[] {
  const seen = new Set<Decoration>();
  for (const l of lines) seen.add(decorationOf(l));
  return (['dtf', 'screen-print', 'mixed'] as const).filter((d) => seen.has(d));
}

/** Compact relative age ("just now", "5m", "3h", "2d", then a date). `nowMs` is injectable for tests. */
export function relativeAge(iso: string, nowMs: number): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const seconds = Math.max(0, Math.round((nowMs - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d`;
  return new Date(iso).toLocaleDateString();
}

/** Local `YYYY-MM-DD` for a timestamp — the shop's own date, so a date-only due never reads overdue
 *  early from a UTC-midnight shift (McAllen is CST). */
function ymdLocal(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Due-date badge label + overdue flag. Null for an absent/unparseable date (mirrors relativeAge's
 * NaN guard — a garbage value never floats to the top or crashes). Compares date-only in shop-local
 * time. `nowMs` is injectable for tests.
 */
export function dueStatus(
  iso: string | null | undefined,
  nowMs: number,
): { label: string; overdue: boolean } | null {
  if (!iso) return null;
  const dueStr = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueStr)) return null;
  const dueMs = Date.parse(`${dueStr}T00:00:00`); // local midnight
  const todayMs = Date.parse(`${ymdLocal(nowMs)}T00:00:00`);
  if (Number.isNaN(dueMs) || Number.isNaN(todayMs)) return null;
  const days = Math.round((dueMs - todayMs) / 86_400_000);
  if (days === 0) return { label: 'due today', overdue: false };
  if (days > 0) return { label: `due in ${days}d`, overdue: false };
  return { label: `${-days}d overdue`, overdue: true };
}

/**
 * The production-queue ordering: rush orders first, then earliest due date (undated last), then FIFO
 * (oldest order first). The final storeId/orderId/lineId tiebreak keeps an order's lines contiguous
 * and makes group emergence deterministic. Pure; sorts a copy. rush/dueDate are order-scoped (shared
 * by all lines of an order), so whole orders move together. Applied client-side over the fully
 * materialized queue (the page loads every page) — the server GSI stays FIFO.
 */
export function sortQueue(lines: readonly OrderLine[]): OrderLine[] {
  return [...lines].sort((a, b) => {
    const ar = a.rush ? 1 : 0;
    const br = b.rush ? 1 : 0;
    if (ar !== br) return br - ar; // rush first
    const ad = a.dueDate ?? null;
    const bd = b.dueDate ?? null;
    if (ad !== bd) {
      if (ad === null) return 1; // undated sorts after dated
      if (bd === null) return -1;
      return ad < bd ? -1 : 1; // earliest due first (ISO strings compare chronologically)
    }
    if (a.shopifyCreatedAt !== b.shopifyCreatedAt) {
      return a.shopifyCreatedAt < b.shopifyCreatedAt ? -1 : 1; // FIFO
    }
    return (
      a.storeId.localeCompare(b.storeId) ||
      a.orderId.localeCompare(b.orderId) ||
      a.lineId.localeCompare(b.lineId)
    );
  });
}

export type QueueSummary = {
  /** Distinct ORDERS in view. The queue is worked order by order, so this is the count an operator
   *  reads first — the line count alone says nothing about how many jobs are waiting. */
  orderCount: number;
  lineCount: number;
  pieceCount: number;
  blocked: number;
  unpaid: number;
  /** Distinct stores present, sorted — powers the store filter. */
  storeIds: string[];
};

export function summarizeQueue(lines: readonly OrderLine[]): QueueSummary {
  const storeIds = new Set<string>();
  const orders = new Set<string>();
  let pieceCount = 0;
  let blocked = 0;
  let unpaid = 0;
  for (const line of lines) {
    storeIds.add(line.storeId);
    // Keyed (store, order): two stores can mint the same order number, and counting them as one
    // would under-report the jobs waiting.
    orders.add(`${line.storeId}#${line.orderId}`);
    pieceCount += line.unfulfilledQty;
    if (line.mappingStatus === 'BLOCKED') blocked += 1;
    if (line.mappingStatus === 'PAYMENT_PENDING') unpaid += 1;
  }
  return {
    orderCount: orders.size,
    lineCount: lines.length,
    pieceCount,
    blocked,
    unpaid,
    storeIds: [...storeIds].sort(),
  };
}

/** Stable selection key for a line (storeId#orderId#lineId). Shared by the page + these helpers. */
export const lineKey = (l: { storeId: string; orderId: string; lineId: string }): string =>
  `${l.storeId}#${l.orderId}#${l.lineId}`;

/**
 * Mirror of the server cap (`packages/rest-api/src/routes/batches.ts` `MAX_BATCH_ITEMS`): a batch's
 * header + one row per placement + the per-line claims must fit ONE DynamoDB transactWrite (limit
 * 100). A line expands to one batch ITEM per placement, so the queue sums selected placements
 * against this to warn BEFORE Build — instead of the operator hitting a 422 after the click.
 */
export const MAX_BATCH_ITEMS = 99;
/** Amber "getting close" threshold for the item counter. */
export const ITEM_CAP_WARN_AT = 85;

/**
 * Batch items a MAPPED line expands to at build. `placementCount` is stamped at ingest; a line that
 * hasn't been re-ingested since the field shipped falls back to 1 (a conservative floor — the server
 * 422 still guards the true cap, so the counter can only warn slightly early, never miss silently).
 */
export function placementCountOf(line: OrderLine): number {
  return line.placementCount ?? 1;
}

/** Total batch items the current selection will expand to (Σ placements over selected lines). */
export function selectedItemCount(
  lines: readonly OrderLine[],
  selected: { has: (key: string) => boolean },
): number {
  let n = 0;
  for (const l of lines) if (selected.has(lineKey(l))) n += placementCountOf(l);
  return n;
}

/**
 * Selection keys with NO matching line in the queue — lines that left while they were selected.
 *
 * ⚠️ `/orders` live-updates from the change feed with no click, so a line another station batched, a
 * reconcile fulfilled, or a sweep closed simply vanishes from the next result. Nothing else
 * reconciles the selection against the data, and the three values derived from it then fail three
 * DIFFERENT ways: `selected.size` OVER-counts, {@link selectedItemCount} silently UNDER-counts (it
 * iterates LINES, so an orphan key contributes 0), and `buildKindFor` can flip `mixed` →
 * `gang-sheet` — re-enabling a Build that was deliberately disabled and erasing the refusal copy
 * with it. The server still answers the whole batch with a 409 so no work is lost; the cost is that
 * the operator is told nothing until after the click.
 *
 * ⚠️ Only act on this once the queue is FULLY loaded. `/orders` pages through the entire queue, so
 * mid-load a line that has simply not arrived yet is indistinguishable from one that left.
 */
/**
 * May the caller act on {@link orphanSelectionKeys} yet?
 *
 * ⚠️ `/orders` loads the WHOLE queue by paging: it calls `fetchNextPage` unconditionally until
 * `hasNextPage` is false, so the line array GROWS between renders. Mid-load, a selected line sitting
 * on a page that has not arrived is indistinguishable from one that left the queue — reconciling
 * then would silently deselect perfectly good work. Split out from the effect so the gate is pinned
 * by name rather than living only inside a `useEffect` nothing renders in a test.
 */
export function shouldReconcileSelection(q: {
  isSuccess: boolean;
  hasNextPage: boolean;
  isFetching: boolean;
}): boolean {
  return q.isSuccess && !q.hasNextPage && !q.isFetching;
}

export function orphanSelectionKeys(
  selected: ReadonlyMap<string, unknown>,
  lines: readonly OrderLine[],
): string[] {
  if (selected.size === 0) return [];
  const live = new Set(lines.map(lineKey));
  return [...selected.keys()].filter((k) => !live.has(k));
}

/**
 * Garments the current selection will press — Σ `unfulfilledQty` over the SELECTED lines.
 *
 * ⚠️ This is the number that means anything on the screen-print lane, where {@link selectedItemCount}
 * is structurally 0: a wholly screen-print line resolves NO DTF placements, so `placementCount` is 0
 * and the "N / 99 print items" readout says 0 however many shirts are in the selection.
 *
 * One row on `/orders` IS one line, so this is a plain sum — not the per-LINE fold `runGarmentCount`
 * must do post-build over placements (a front+back line is two placements and one shirt). It equals
 * that fold by construction: the builder stamps a work item's quantity from
 * `qtyOverride ?? line.unfulfilledQty` and `/orders` submits bare refs carrying no quantity override,
 * so the number shown before the build is the number the run reports after it.
 */
export function selectedGarmentCount(
  lines: readonly OrderLine[],
  selected: { has: (key: string) => boolean },
): number {
  let n = 0;
  for (const l of lines) if (selected.has(lineKey(l))) n += l.unfulfilledQty;
  return n;
}

/**
 * Narrow a mixed-technique selection to ONE buildable half, so the D2 refusal can offer the fix
 * instead of describing it.
 *
 * ⚠️ The split mirrors `buildKindFor` EXACTLY: only a wholly screen-print line is filmless, so a
 * `mixed` line counts as FILM — it has film and builds a gang sheet like any DTF line. Splitting on
 * "is not dtf" instead would strand every mixed line in neither half, and the operator would press a
 * fix button and still be refused.
 */
export function keepOnlyTechnique(
  lines: readonly OrderLine[],
  selected: ReadonlyMap<string, LineRef>,
  keep: 'screen-print' | 'film',
): Map<string, LineRef> {
  const next = new Map<string, LineRef>();
  for (const l of lines) {
    const k = lineKey(l);
    if (!selected.has(k)) continue;
    const filmless = decorationOf(l) === 'screen-print';
    if (filmless === (keep === 'screen-print')) {
      next.set(k, { storeId: l.storeId, orderId: l.orderId, lineId: l.lineId });
    }
  }
  return next;
}

/** One order's contribution to the sheet: its designs, and the batch items each will cost. */
export type SheetOrder = {
  orderName: string;
  designs: { title: string; items: number }[];
};

/**
 * "On this sheet" — what the current selection actually contains, grouped order → design.
 *
 * The selection bar could only ever say how MANY items were on the sheet. This says WHICH, which is
 * the question an operator asks when the meter reads 89 and they have to decide what to drop.
 * Items are `placementCountOf`, the same number the meter totals, so the rows visibly sum to it.
 */
export function sheetContents(
  lines: readonly OrderLine[],
  selected: ReadonlyMap<string, unknown>,
): SheetOrder[] {
  const byOrder = new Map<string, { orderName: string; byTitle: Map<string, number> }>();
  for (const l of lines) {
    if (!selected.has(lineKey(l))) continue;
    const k = `${l.storeId}#${l.orderId}`;
    let o = byOrder.get(k);
    if (!o) {
      o = { orderName: l.orderName, byTitle: new Map() };
      byOrder.set(k, o);
    }
    o.byTitle.set(l.title, (o.byTitle.get(l.title) ?? 0) + placementCountOf(l));
  }
  return [...byOrder.values()].map((o) => ({
    orderName: o.orderName,
    designs: [...o.byTitle].map(([title, items]) => ({ title, items })),
  }));
}

/** An order that is not (fully) selected but could join this sheet. */
export type FitCandidate = {
  key: string;
  label: string;
  /** Batch items the unselected remainder of this order would add. */
  need: number;
  /** Does that remainder fit in what is left of the 99? */
  fits: boolean;
  /** Every ready line of the order — Add selects the whole order, as the queue checkbox does. */
  refs: LineRef[];
};

/**
 * "Would still fit" — orders that could top up a part-filled sheet, and whether they fit.
 *
 * ⚠️ The one genuinely NEW affordance in the redesign: nothing in the queue today tells an operator
 * what would fill the remaining headroom, so a sheet gets built at 74 / 99 and the next one carries
 * work that could have ridden along. Advisory — Add just selects, exactly as ticking the order would.
 *
 * Scoped to the selection's OWN store (a gang sheet never mixes brands, mirroring
 * {@link selectionStoreId}) and to FILM lines only: this list belongs to the sheet state, and a
 * screen-print line costs 0 items, so it would always "fit" while contributing nothing to the meter.
 */
export function fitCandidates(
  lines: readonly OrderLine[],
  selected: ReadonlyMap<string, unknown>,
  headroom: number,
  storeId: string | undefined,
): FitCandidate[] {
  const byOrder = new Map<string, OrderLine[]>();
  for (const l of lines) {
    if (l.mappingStatus !== 'MAPPED') continue;
    if (decorationOf(l) === 'screen-print') continue;
    if (storeId && l.storeId !== storeId) continue;
    const k = `${l.storeId}#${l.orderId}`;
    const g = byOrder.get(k);
    if (g) g.push(l);
    else byOrder.set(k, [l]);
  }
  const out: FitCandidate[] = [];
  for (const [k, ready] of byOrder) {
    const missing = ready.filter((l) => !selected.has(lineKey(l)));
    // No remainder ⇒ the order is already wholly on the sheet, so there is nothing to offer. This is
    // the ONLY guard for that case; a second `missing.length === 0` check above it would be dead code
    // and would make this one untestable.
    const first = missing[0];
    if (!first) continue;
    const need = missing.reduce((n, l) => n + placementCountOf(l), 0);
    out.push({
      key: k,
      label: `${first.orderName} · ${first.title}`,
      need,
      fits: need <= headroom,
      refs: ready.map((l) => ({ storeId: l.storeId, orderId: l.orderId, lineId: l.lineId })),
    });
  }
  return out.sort((a, z) => a.need - z.need || a.label.localeCompare(z.label));
}

/**
 * The single store every selected line belongs to, or undefined when the selection is empty. A gang
 * sheet is single-store by policy (never mix brands' transfers on one physical sheet — mispress +
 * cross-liability), and the toggle helpers below enforce that invariant, so the first entry's store
 * speaks for the whole selection.
 */
export function selectionStoreId(selected: ReadonlyMap<string, LineRef>): string | undefined {
  for (const ref of selected.values()) return ref.storeId;
  return undefined;
}

const refOf = (l: LineRef): LineRef => ({
  storeId: l.storeId,
  orderId: l.orderId,
  lineId: l.lineId,
});

/**
 * Toggle one line in the selection, enforcing the single-store invariant: selecting a line from a
 * DIFFERENT store than the current selection starts a FRESH selection for that store rather than
 * adding across stores (a sheet can't mix brands). Deselection only ever happens within the current
 * store. Returns a new Map.
 */
export function toggleLineSelection(
  selected: ReadonlyMap<string, LineRef>,
  line: LineRef,
): Map<string, LineRef> {
  const k = lineKey(line);
  const cur = selectionStoreId(selected);
  if (cur && cur !== line.storeId) return new Map([[k, refOf(line)]]);
  const next = new Map(selected);
  if (next.has(k)) next.delete(k);
  else next.set(k, refOf(line));
  return next;
}

/**
 * Toggle all of one order's ready (MAPPED) lines at once, with the same single-store enforcement: an
 * order from a different store than the current selection REPLACES it (its ready lines become the new
 * single-store selection). Within the current store it toggles the whole group on/off. Returns a new Map.
 */
export function toggleGroupSelection(
  selected: ReadonlyMap<string, LineRef>,
  group: { storeId: string; readyLines: readonly LineRef[] },
): Map<string, LineRef> {
  const cur = selectionStoreId(selected);
  if (cur && cur !== group.storeId) {
    return new Map(group.readyLines.map((l) => [lineKey(l), refOf(l)]));
  }
  const next = new Map(selected);
  const allSel = group.readyLines.length > 0 && group.readyLines.every((l) => next.has(lineKey(l)));
  for (const l of group.readyLines) {
    if (allSel) next.delete(lineKey(l));
    else next.set(lineKey(l), refOf(l));
  }
  return next;
}

/** Result of {@link pickInStockUpToCap} — the refs to select plus why the rest were left out. */
export type InStockPick = {
  /** Lines to select — deliverable from stock, in work-priority order, fitting the item cap. */
  refs: LineRef[];
  /** Batch items (Σ placements) the chosen refs expand to. */
  items: number;
  /** Ready lines dropped because they're short on blank stock (from the coverage advisory). */
  shortSkipped: number;
  /** Buildable lines left for a later sheet because this one hit the item cap. */
  capDeferred: number;
  /** Buildable lines that alone exceed the cap — can't sit on one sheet; need a manual split. */
  oversized: number;
};

/**
 * Choose which ready lines a "select what's in stock" click should select. Walks the store's ready
 * lines in the order given (feed it work-priority order — rush ↓, due ↑, FIFO), drops any that are
 * short on blanks (`shortKeys`, keyed via {@link lineKey} from the coverage advisory), and greedily
 * fills the selection up to `cap` batch items so the result is immediately buildable (never over-cap).
 *
 * Priority is preserved: it takes a top-of-queue prefix and STOPS at the first buildable line that
 * won't fit the remaining space — it does NOT skip ahead to grab a smaller lower-priority line (that
 * would build later work ahead of a rush/older order). A single line larger than the whole cap can
 * never sit on one sheet, so it's skipped (counted `oversized`, not a stop) and the fill continues.
 *
 * Pure. Trimming only ever REMOVES demand, and removing demand can't un-cover a covered line, so
 * every returned ref stays fully deliverable regardless of which lines the cap dropped.
 */
export function pickInStockUpToCap(
  scopedReady: readonly OrderLine[],
  shortKeys: ReadonlySet<string>,
  cap: number = MAX_BATCH_ITEMS,
): InStockPick {
  const refs: LineRef[] = [];
  let items = 0;
  let shortSkipped = 0;
  let capDeferred = 0;
  let oversized = 0;
  // Set once we hit a line that fits an empty sheet but not the space left — everything after it
  // defers, so we don't reorder work by reaching past it for something smaller.
  let full = false;
  for (const line of scopedReady) {
    if (shortKeys.has(lineKey(line))) {
      shortSkipped += 1;
      continue;
    }
    const n = placementCountOf(line);
    if (full) {
      capDeferred += 1;
    } else if (items + n <= cap) {
      refs.push(refOf(line));
      items += n;
    } else if (n > cap) {
      oversized += 1; // too big for any single sheet — skip it, keep filling with the rest
    } else {
      full = true; // stop the fill here; preserve priority order
      capDeferred += 1;
    }
  }
  return { refs, items, shortSkipped, capDeferred, oversized };
}

/** A queue order group: all lines of one Shopify order, plus its batchable (MAPPED) subset. */
export type OrderGroup = {
  /** `storeId#orderId` — stable group key. */
  key: string;
  storeId: string;
  orderId: string;
  orderName: string;
  shopifyCreatedAt: string;
  /** Order carries the allow-listed rush tag (any line → whole order; order-scoped). Drives the badge. */
  rush: boolean;
  /** Earliest due date across the order's lines (ISO `YYYY-MM-DD`), or null. Drives the due badge. */
  dueDate: string | null;
  /** Every line of the order, in server order. */
  lines: OrderLine[];
  /** The batchable (MAPPED) lines — what "select order" toggles. */
  readyLines: OrderLine[];
  /** Batch items this order contributes if all its ready lines are selected (Σ placements). */
  itemCount: number;
};

/**
 * Group queue lines by order, preserving input order. Feed it {@link sortQueue}'s output so groups
 * emerge in priority order (rush ↓, dueDate ↑, then FIFO): rush/dueDate are order-scoped and the sort
 * keeps an order's lines contiguous, so the highest-priority whole orders sit at the top.
 */
export function groupByOrder(lines: readonly OrderLine[]): OrderGroup[] {
  const groups = new Map<string, OrderGroup>();
  for (const line of lines) {
    const key = `${line.storeId}#${line.orderId}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        storeId: line.storeId,
        orderId: line.orderId,
        orderName: line.orderName,
        shopifyCreatedAt: line.shopifyCreatedAt,
        rush: false,
        dueDate: null,
        lines: [],
        readyLines: [],
        itemCount: 0,
      };
      groups.set(key, g);
    }
    // rush/dueDate are order-scoped (identical across an order's lines today); aggregate defensively:
    // any line rush → rush; the earliest non-null dueDate.
    if (line.rush) g.rush = true;
    if (line.dueDate && (g.dueDate === null || line.dueDate < g.dueDate)) g.dueDate = line.dueDate;
    g.lines.push(line);
    if (line.mappingStatus === 'MAPPED') {
      g.readyLines.push(line);
      g.itemCount += placementCountOf(line);
    }
  }
  return [...groups.values()];
}
